import React, { useMemo } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import { cn } from '../../lib/utils';
import type { Column } from '../../shared/types';

interface VirtualizedTableProps<T> {
  data: T[];
  columns: Column<T>[];
  height?: number;
  itemHeight?: number;
  className?: string;
  onRowClick?: (item: T) => void;
}

export function VirtualizedTable<T extends { id: string }>({
  data,
  columns,
  height = 400,
  itemHeight = 60,
  className,
  onRowClick,
}: VirtualizedTableProps<T>) {
  const parentRef = React.useRef<HTMLDivElement>(null);

  const virtualizer = useVirtualizer({
    count: data.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => itemHeight,
    overscan: 5, // Render 5 extra items outside viewport for smooth scrolling
  });

  const items = virtualizer.getVirtualItems();

  // Memoize column widths calculation
  const columnWidths = useMemo(() => {
    return columns.map(col => col.width || 'auto');
  }, [columns]);

  return (
    <div className={cn('border rounded-md', className)}>
      {/* Header */}
      <div className="border-b bg-muted/50">
        <div 
          className="grid items-center px-4 py-3 font-medium text-sm"
          style={{
            gridTemplateColumns: columnWidths.map(w => 
              typeof w === 'number' ? `${w}px` : w
            ).join(' '),
          }}
        >
          {columns.map((column) => (
            <div
              key={column.key}
              className={cn(
                'truncate',
                column.align === 'center' && 'text-center',
                column.align === 'right' && 'text-right'
              )}
            >
              {column.title}
            </div>
          ))}
        </div>
      </div>

      {/* Virtualized Body */}
      <div
        ref={parentRef}
        className="overflow-auto"
        style={{ height }}
      >
        <div
          style={{
            height: `${virtualizer.getTotalSize()}px`,
            width: '100%',
            position: 'relative',
          }}
        >
          {items.map((virtualItem) => {
            const item = data[virtualItem.index];
            return (
              <div
                key={item.id}
                className={cn(
                  'absolute top-0 left-0 w-full border-b hover:bg-muted/50 transition-colors',
                  onRowClick && 'cursor-pointer'
                )}
                style={{
                  height: `${virtualItem.size}px`,
                  transform: `translateY(${virtualItem.start}px)`,
                }}
                onClick={() => onRowClick?.(item)}
              >
                <div 
                  className="grid items-center px-4 py-3 h-full"
                  style={{
                    gridTemplateColumns: columnWidths.map(w => 
                      typeof w === 'number' ? `${w}px` : w
                    ).join(' '),
                  }}
                >
                  {columns.map((column) => (
                    <div
                      key={column.key}
                      className={cn(
                        'truncate',
                        column.align === 'center' && 'text-center',
                        column.align === 'right' && 'text-right'
                      )}
                    >
                      {column.render 
                        ? column.render(item[column.key as keyof T], item, virtualItem.index)
                        : String(item[column.key as keyof T] || '')
                      }
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// Hook for determining when to use virtualization
export const useVirtualization = (itemCount: number, threshold: number = 100) => {
  return useMemo(() => ({
    shouldVirtualize: itemCount > threshold,
    itemCount,
  }), [itemCount, threshold]);
};